<template>
  <div
    :class="['email-component', 'game-master-component', { selected }]"
    :style="computedStyles"
    @click="handleClick"
  >
    <div class="game-master-wrapper" :style="wrapperStyles">
      <!-- Image Container -->
      <div class="game-master-image-container" :style="imageContainerStyles">
        <div class="placeholder-image" :style="imagePlaceholderStyles">
          <i class="tim-icons icon-single-02"></i>
        </div>
      </div>

      <!-- Content Container -->
      <div class="game-master-content" :style="contentStyles">
        <!-- Name/Nickname -->
        <div class="game-master-name" :style="nameStyles">
          {{ displayName }}
        </div>

        <!-- Role/Title (optional) -->
        <div
          v-if="componentData.showRole"
          class="game-master-role"
          :style="roleStyles"
        >
          {{ getLabel('gameMaster') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GameMasterComponent',
  props: ['componentData', 'selected'],
  data() {
    return {
      labels: {
        english: {
          gameMaster: 'Game Master',
          placeholder: 'Game Master Name'
        },
        spanish: {
          gameMaster: 'Maestro de Juego',
          placeholder: 'Nombre del Maestro'
        },
        french: {
          gameMaster: 'Maître du Jeu',
          placeholder: 'Nom du Maître'
        },
        swedish: {
          gameMaster: 'Spelledare',
          placeholder: 'Spelledarens Namn'
        },
        danish: {
          gameMaster: 'Spilleder',
          placeholder: 'Spilleders navn'
        },
        greek: {
          gameMaster: 'Αρχηγός Παιχνιδιού',
          placeholder: 'Όνομα Αρχηγού'
        }
      }
    }
  },
  computed: {
    currentLanguage() {
      return this.componentData.language || 'english'
    },

    currentLayout() {
      return this.componentData.layout || 'image-left'
    },

    displayName() {
      // In actual rendering, this will be replaced with real data
      // componentData.displayField will be either 'name' or 'nickname'
      return this.getLabel('placeholder')
    },

    computedStyles() {
      const styles = this.componentData.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      return {
        backgroundColor: backgroundColor,
        padding: `${styles.paddingTop || '20'}px ${styles.paddingLeft || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingRight || '20'}px`,
        marginTop: `${styles.marginTop || '16'}px`,
        marginBottom: `${styles.marginBottom || '16'}px`,
        borderRadius: `${styles.borderRadius || '8'}px`,
        border: styles.showBorder ? `1px solid ${styles.borderColor || '#e5e7eb'}` : 'none',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    wrapperStyles() {
      const layout = this.currentLayout

      if (layout === 'image-top') {
        return {
          display: 'flex',
          flexDirection: 'column',
          alignItems: this.componentData.alignment || 'center',
          gap: '12px'
        }
      }

      return {
        display: 'flex',
        flexDirection: layout === 'image-left' ? 'row' : 'row-reverse',
        gap: '16px',
        alignItems: 'center'
      }
    },

    imageContainerStyles() {
      const styles = this.componentData.styles || {}

      return {
        flexShrink: '0'
      }
    },

    imagePlaceholderStyles() {
      const styles = this.componentData.styles || {}
      const imageSize = styles.imageSize || '80'

      return {
        width: `${imageSize}px`,
        height: `${imageSize}px`,
        borderRadius: styles.imageShape === 'square'
          ? `${styles.imageBorderRadius || '8'}px`
          : '50%',
        backgroundColor: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#9ca3af',
        fontSize: `${parseInt(imageSize) * 0.4}px`,
        border: styles.showImageBorder ? `2px solid ${styles.imageBorderColor || '#e5e7eb'}` : 'none'
      }
    },

    contentStyles() {
      const layout = this.currentLayout

      if (layout === 'image-top') {
        return {
          textAlign: this.componentData.alignment || 'center',
          width: '100%'
        }
      }

      return {
        flex: '1',
        minWidth: '0'
      }
    },

    nameStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.nameFontSize || '18'}px`,
        fontWeight: styles.nameFontWeight || '600',
        color: styles.nameColor || '#111827',
        margin: '0',
        fontFamily: styles.fontFamily || 'Arial, sans-serif',
        lineHeight: '1.3'
      }
    },

    roleStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.roleFontSize || '13'}px`,
        fontWeight: styles.roleFontWeight || '400',
        color: styles.roleColor || '#6b7280',
        margin: '4px 0 0 0',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    }
  },
  methods: {
    handleClick() {
      this.$emit('click')
    },

    getLabel(key) {
      const languageLabels = this.labels[this.currentLanguage] || this.labels.english
      return languageLabels[key] || key
    }
  }
}
</script>

<style scoped>
.game-master-component {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  width: 100%;
  box-sizing: border-box;
}

.game-master-component:hover,
.game-master-component.selected {
  border-color: #e14eca !important;
  box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.1);
}

.game-master-wrapper {
  width: 100%;
}

.placeholder-image {
  overflow: hidden;
}

.game-master-content {
  word-wrap: break-word;
}

.game-master-name,
.game-master-role {
  word-wrap: break-word;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .game-master-wrapper {
    flex-direction: column !important;
    align-items: center !important;
  }

  .game-master-content {
    text-align: center !important;
  }
}
</style>
